import { API_ENDPOINTS } from "@/configs";
import { track } from "@/lib/analytics";
import apiRequest from "@/lib/axios";
import { getRecaptchaToken } from "@/lib/recaptcha";
import {
  Email,
  EmailPassword,
  LoginUserRes,
  Success,
  TwoFactor,
  User,
  UserForm,
  UserProfile,
} from "@/types";
import { AffSubIds, SignupAttribution } from "@/types/auth";
import { GooglePublicConfig } from "@/types/google";
import { TelegramLinkStatus, TelegramPublicConfig } from "@/types/telegram";
import { TonProofLogin, TonPublicConfig } from "@/types/ton";

const postForSuccess = (url: string, errorMessage: string, data?: unknown): Promise<boolean> =>
  apiRequest<Success>({
    method: "POST",
    url,
    data,
    errorMessage,
  }).then((res) => res.success);

// Counted once the session exists: a 2FA challenge is counted when verifyLogin2fa passes.
const signedIn =
  (event: "login" | "sign_up", method: string) =>
  (res: LoginUserRes): LoginUserRes => {
    if (!res.requires2fa) track(event, { method });
    return res;
  };

export const setup2FA = async (): Promise<TwoFactor> => {
  return apiRequest({
    method: "POST",
    url: API_ENDPOINTS.AUTH.SETUP_2FA,
    errorMessage: "Failed to start 2FA setup",
  });
};

export const verify2faSetup = async (data: {
  token: string;
}): Promise<{ success: boolean; backupCodes?: string[] }> => {
  return apiRequest({
    method: "POST",
    url: API_ENDPOINTS.AUTH.VERIFY_2FA_SETUP,
    data,
    errorMessage: "Failed to verify 2FA code",
  });
};

// Needs a live authenticator code; the old codes stop working.
export const regenerateBackupCodes = async (data: {
  code: string;
}): Promise<{ backupCodes: string[] }> => {
  return apiRequest({
    method: "POST",
    url: API_ENDPOINTS.AUTH.BACKUP_CODES_2FA,
    data,
    errorMessage: "Failed to generate backup codes",
  });
};

// Turning 2FA off needs a current authenticator code or a backup code.
export const disable2FA = async (data: { code: string }): Promise<boolean> => {
  return postForSuccess(API_ENDPOINTS.AUTH.DISABLE_2FA, "Failed to disable 2FA", data);
};

export const get2FAStatus = async (): Promise<{ enabled: boolean; backupCodesLeft: number }> => {
  return apiRequest({
    method: "GET",
    url: API_ENDPOINTS.AUTH.STATUS_2FA,
    errorMessage: "Failed to load 2FA status",
  });
};

export const registerUser = async (data: UserForm): Promise<LoginUserRes> => {
  return apiRequest<LoginUserRes>({
    method: "POST",
    url: API_ENDPOINTS.AUTH.REGISTER,
    data: { ...data, recaptchaToken: await getRecaptchaToken("register") },
    errorMessage: "Registration failed",
  }).then(signedIn("sign_up", "email"));
};

export const loginUser = async (data: EmailPassword): Promise<LoginUserRes> => {
  return apiRequest<LoginUserRes>({
    method: "POST",
    url: API_ENDPOINTS.AUTH.LOGIN,
    data: { ...data, recaptchaToken: await getRecaptchaToken("login") },
    errorMessage: "Login failed",
  }).then(signedIn("login", "email"));
};

export const verifyLogin2fa = async (data: {
  pendingToken: string;
  code: string;
}): Promise<LoginUserRes> => {
  return apiRequest<LoginUserRes>({
    method: "POST",
    url: API_ENDPOINTS.AUTH.VERIFY_2FA,
    data,
    errorMessage: "Verification failed",
  }).then(signedIn("login", "2fa"));
};

// Ends the session on the server (Redis, restore cookie, live sockets), not just in this tab.
export const logoutUser = async (): Promise<void> => {
  await apiRequest({
    method: "POST",
    url: API_ENDPOINTS.AUTH.LOGOUT,
    timeout: 8000,
    errorMessage: "Logout failed",
  });
  track("logout");
};

export const forgotPassword = async (data: Email): Promise<boolean> => {
  return postForSuccess(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, "ForgotPassword failed", data);
};

export const getMyProfile = async (): Promise<UserProfile | null> => {
  return apiRequest({
    method: "GET",
    url: API_ENDPOINTS.AUTH.PROFILE,
    errorMessage: "Failed to load profile",
  });
};

export const updateMyProfile = async (data: Partial<UserProfile>): Promise<UserProfile> => {
  return apiRequest({
    method: "PUT",
    url: API_ENDPOINTS.AUTH.PROFILE,
    data,
    errorMessage: "Failed to update profile",
  });
};

export const changePassword = async (data: {
  oldPassword: string;
  newPassword: string;
}): Promise<boolean> => {
  return postForSuccess(API_ENDPOINTS.AUTH.CHANGE_PASSWORD, "Failed to change password", data);
};

export const getPasswordStatus = async (): Promise<{ hasPassword: boolean }> => {
  return apiRequest({
    method: "GET",
    url: API_ENDPOINTS.AUTH.PASSWORD_STATUS,
    errorMessage: "Failed to load password status",
  });
};

export const setPassword = async (data: { newPassword: string }): Promise<boolean> => {
  return postForSuccess(API_ENDPOINTS.AUTH.SET_PASSWORD, "Failed to set password", data);
};

export const resetPassword = async (data: {
  token: string;
  password: string;
}): Promise<boolean> => {
  return postForSuccess(API_ENDPOINTS.AUTH.RESET_PASSWORD, "Failed to reset password", data);
};

export const updateUserRequest = async (data: Partial<User>): Promise<User> => {
  return apiRequest({
    method: "PUT",
    url: API_ENDPOINTS.AUTH.UPDATE,
    data,
    errorMessage: "Failed to update user",
  });
};

export const requestEmailChange = async (data: {
  email: string;
  password?: string;
  totpCode?: string;
}): Promise<{ status: "confirm_sent" | "changed"; email?: string }> => {
  return apiRequest({
    method: "POST",
    url: API_ENDPOINTS.AUTH.EMAIL_CHANGE,
    data,
    errorMessage: "Failed to change email",
  });
};

export const confirmEmailChange = async (token: string): Promise<{ changed: boolean }> => {
  return apiRequest({
    method: "POST",
    url: API_ENDPOINTS.AUTH.EMAIL_CHANGE_CONFIRM,
    data: { token },
    errorMessage: "Failed to change email",
  });
};

/**
 * Signs in from a Telegram Mini App launch. `initData` is the signed string
 * Telegram handed the page; the backend checks its HMAC and opens an account on
 * first sight, so this one call covers both sign-up and sign-in.
 *
 * The answer has the same shape as a password login, `requires2fa` included -
 * holding the Telegram account is not a way past an authenticator.
 */
export const loginWithTelegram = async (data: {
  initData: string;
  referralCode?: string;
  affCode?: string;
  clickId?: string;
  affSubIds?: AffSubIds;
  attribution?: SignupAttribution;
  language?: string;
}): Promise<LoginUserRes> => {
  return apiRequest<LoginUserRes>({
    method: "POST",
    url: API_ENDPOINTS.AUTH.TELEGRAM_LOGIN,
    data,
    errorMessage: "Telegram sign-in failed",
  }).then(signedIn("login", "telegram"));
};

/**
 * Signs in from a Telegram **Login Widget** redirect, the web counterpart of
 * the call above.
 *
 * `authQuery` is the raw query string Telegram appended to the page URL, passed
 * through as text and never parsed into an object on the way: Telegram ids are
 * 64-bit and a JSON number would round anything past 2^53, storing the account
 * link against the wrong person.
 */
export const loginWithTelegramWidget = async (data: {
  authQuery: string;
  referralCode?: string;
  affCode?: string;
  clickId?: string;
  affSubIds?: AffSubIds;
  attribution?: SignupAttribution;
  language?: string;
}): Promise<LoginUserRes> => {
  return apiRequest<LoginUserRes>({
    method: "POST",
    url: API_ENDPOINTS.AUTH.TELEGRAM_WIDGET_LOGIN,
    data,
    errorMessage: "Telegram sign-in failed",
  }).then(signedIn("login", "telegram"));
};

/** Whether to offer Telegram sign-in, and for which bot. Needs no session. */
export const getTelegramConfig = async (): Promise<TelegramPublicConfig> => {
  return apiRequest({
    method: "GET",
    url: API_ENDPOINTS.AUTH.TELEGRAM_CONFIG,
    errorMessage: "Failed to load Telegram configuration",
  });
};

/** Attaches Telegram to the account already signed in, so no second one is made. */
export const linkTelegram = async (data: { initData: string }): Promise<TelegramLinkStatus> => {
  return apiRequest({
    method: "POST",
    url: API_ENDPOINTS.AUTH.TELEGRAM_LINK,
    data,
    errorMessage: "Failed to connect Telegram",
  });
};

/** The same, from a web Login Widget sign-in instead of a Mini App launch. */
export const linkTelegramWidget = async (data: {
  authQuery: string;
}): Promise<TelegramLinkStatus> => {
  return apiRequest({
    method: "POST",
    url: API_ENDPOINTS.AUTH.TELEGRAM_WIDGET_LINK,
    data,
    errorMessage: "Failed to connect Telegram",
  });
};

export const unlinkTelegram = async (): Promise<TelegramLinkStatus> => {
  return apiRequest({
    method: "POST",
    url: API_ENDPOINTS.AUTH.TELEGRAM_UNLINK,
    errorMessage: "Failed to disconnect Telegram",
  });
};

export const getTelegramStatus = async (): Promise<TelegramLinkStatus> => {
  return apiRequest({
    method: "GET",
    url: API_ENDPOINTS.AUTH.TELEGRAM_STATUS,
    errorMessage: "Failed to load Telegram status",
  });
};

/**
 * Signs in with the ID token the Google button handed the page. The backend
 * checks its signature and opens an account on first sight, so this one call
 * covers both sign-up and sign-in.
 *
 * The answer has the same shape as a password login, `requires2fa` included -
 * holding the Google account is not a way past an authenticator.
 */
export const loginWithGoogle = async (data: {
  credential: string;
  referralCode?: string;
  affCode?: string;
  clickId?: string;
  affSubIds?: AffSubIds;
  attribution?: SignupAttribution;
  language?: string;
}): Promise<LoginUserRes> => {
  return apiRequest<LoginUserRes>({
    method: "POST",
    url: API_ENDPOINTS.AUTH.GOOGLE_LOGIN,
    data,
    errorMessage: "Google sign-in failed",
  }).then(signedIn("login", "google"));
};

/** Whether to offer Google sign-in, and for which OAuth client. Needs no session. */
export const getGoogleConfig = async (): Promise<GooglePublicConfig> => {
  return apiRequest({
    method: "GET",
    url: API_ENDPOINTS.AUTH.GOOGLE_CONFIG,
    errorMessage: "Failed to load Google configuration",
  });
};

/** Whether to offer TON wallet sign-in on this site. Needs no session. */
export const getTonConfig = async (): Promise<TonPublicConfig> => {
  return apiRequest({
    method: "GET",
    url: API_ENDPOINTS.AUTH.TON_CONFIG,
    params: { domain: window.location.host },
    errorMessage: "Failed to load TON configuration",
  });
};

/** The challenge a wallet has to sign to prove it is signing in here, now. */
export const getTonProofPayload = async (): Promise<string> => {
  const { payload } = await apiRequest<{ payload: string }>({
    method: "POST",
    url: API_ENDPOINTS.AUTH.TON_PAYLOAD,
    errorMessage: "TON sign-in could not be started",
  });

  return payload;
};

/**
 * Signs in with the proof a TON wallet signed. The backend checks the
 * signature and opens an account on first sight, so this one call covers both
 * sign-up and sign-in.
 *
 * The answer has the same shape as a password login, `requires2fa` included -
 * holding the wallet is not a way past an authenticator.
 */
export const loginWithTon = async (
  data: TonProofLogin & {
    referralCode?: string;
    affCode?: string;
    clickId?: string;
    affSubIds?: AffSubIds;
    attribution?: SignupAttribution;
    language?: string;
  },
): Promise<LoginUserRes> => {
  return apiRequest<LoginUserRes>({
    method: "POST",
    url: API_ENDPOINTS.AUTH.TON_LOGIN,
    data,
    errorMessage: "TON sign-in failed",
  }).then(signedIn("login", "ton"));
};
